import React, {useEffect, useState} from 'react';
import {Button, Col, Container, Row} from 'react-bootstrap';
import {useParams} from "react-router-dom";
import { ConfirmationDialog} from "./ConfirmationDialog";
import {GenericModelViewer} from "./GenericModelViewer";

import {Glyphicon} from "./GlyphIcon";

import {ObjectUtils} from "../../Utils";
import {useChangePage} from "../../Hooks/NavigationHooks";

import {
    useCheckAuth,
} from  "../../Hooks/RouteHooks";
import {        
    useMakePageRequest,
} from "../../Hooks/RequestHooks";

import {useClassNames} from "../../Hooks/StyleHooks";
import {useContextIndependentPage} from "../../Hooks/ContextBuilderHooks";

import {StreamBankPageHeader} from "./StreamBankPageHeader";

import * as models from '../../Models/StreamBank';
import {useValidations} from "../../Hooks/GenericModelHooks";


export const ServiceDrivenViewPage = ((Service) => function ServiceDrivenViewPage() {
    useCheckAuth();
    useContextIndependentPage();
    const getPageName = () => {
        const pageName = Service.ModelName.replace(/\s+/g, '-').toLocaleLowerCase();

        return `sb-${pageName}-view-page`;
    };

    const classNames = useClassNames(getPageName());
    const makePageRequest = useMakePageRequest();
    const changePage = useChangePage();

    const [model, setModel] = useState(null);
    const [existingModel, setExistingModel] = useState(null);
    const [validation, setValidation, isValid] = useValidations([]);
    const [editMode, setEditMode] = useState(false);
    const [deleteDialog, setDeleteDialog] = useState(false);

    const params = useParams();
    const modelId = params[Service.PrimaryKeyProperty];

    useEffect(() => {
        loadData();
    }, [modelId]);


    const loadData = () => {
        if(modelId === "new") {
            let defaultModel = {};

            if (typeof Service.GetDefaultModel === 'function') {
                defaultModel = Service.GetDefaultModel();
            }

            setEditMode(true);
            setModel(defaultModel);
        } else {
            makePageRequest(
                () => Service.GetModel(modelId),
                (x) => {
                    setModel(x.result);
                    setEditMode(false);
                }
            );
        }
    };

    const enterEditMode = () => {
        setEditMode(true);
        setExistingModel(ObjectUtils.Clone(model));
    };

    const exitEditMode = () => {
        if(existingModel) {
            setEditMode(false);
            setModel(ObjectUtils.Clone(existingModel));
            setExistingModel(null);
        } else {
            let newPath = window.location.pathname.replace(`${Service.PageUrl}/new`, Service.PageUrl);
            changePage(newPath);
        }
    };

    const update = (model) => {
        setModel(model);
    };

    const save = () => {
        if(model[Service.PrimaryKeyProperty]) {
            makePageRequest(
                () => Service.UpdateModel(model),
                (x) => {
                    setEditMode(false);
                    setModel(x.result);
                }
            );
        } else {
            makePageRequest(
                () => Service.CreateModel(model),
                (x) => {
                    let newPath = window.location.pathname.replace("new", x.result[Service.PrimaryKeyProperty]);
                    changePage(newPath);
                }
            );
        }
    };

    const deleteModel = () => {
        makePageRequest(
            () => Service.DeleteModel(model[Service.PrimaryKeyProperty]),
            () => {
                changePage(Service.PageUrl);
            }
        );
    };
    
    const getModelInstanceByService = (service) => {
        const model = models?.[service?.ModelName?.replace(/ /g, '')];
        return model ? new model() : null;
    }

    // don't render anything until we are loaded.
    if(model === null) {
        return null;
    }

    return <Container className={classNames}>
        <Row>
            <StreamBankPageHeader
                Title={model[Service.TitleProperty]}
            />
            <ConfirmationDialog
                Title="Confirmation"
                Body={`Are you sure you want to delete this ${Service.ModelName}?`}
                Display={deleteDialog}
                OnClose={() => {
                    setDeleteDialog(false);
                }}
                OnConfirm={() => {
                    deleteModel();
                }}
            />
            <Col xs={12} md={6}>
                <h2 className="atomic-sectionheader">
                    <div className="float-right btn-edit">
                        {
                            editMode ? [
                                    <Button
                                        variant="save"
                                        key={1}
                                        onClick={() => save()}
                                        disabled={!isValid}
                                    >
                                        Save
                                    </Button>,
                                    <Button
                                        variant="cancel"
                                        key={2}
                                        onClick={() => exitEditMode()}
                                    >
                                        Cancel
                                    </Button>,
                                    modelId !== 'new' ?
                                        <Button
                                            variant="delete"
                                            key={3}
                                            onClick={() => setDeleteDialog(true)}
                                        >
                                            Delete
                                        </Button> :
                                        null
                                ] :
                                <Button
                                    variant="link"
                                    onClick={() => enterEditMode()}>
                                    <Glyphicon glyph="pencil" />Edit
                                </Button>
                        }
                    </div>
                    {Service.ModelName}: {model ? model[Service.TitleProperty] : ""}
                </h2>

                {
                    model ?
                        <GenericModelViewer
                            IsNewModel={modelId === "new"}
                            EditMode={editMode}
                            Data={model}
                            Update={update}
                            OnValidationUpdate={setValidation}
                            Model={getModelInstanceByService(Service)}
                        />:
                        null
                }
            </Col>
            <Col xs={12} md={6} />
        </Row>
    </Container>;
});
